import {ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal} from '@angular/core';
import {DatePipe} from '@angular/common';
import {MessageService} from 'primeng/api';
import {finalize} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

import {MonitoringClient} from '../../api-client/api/api';
import {silentErrors} from '../../shared/http-context';
import {JobFreshnessDto} from '../../api-client/model/job-freshness-dto';
import {MonitoringStatusDto} from '../../api-client/model/monitoring-status-dto';
import {MonitoringStateDto} from '../../api-client/model/monitoring-state-dto';
import {DATE_TIME_FORMAT} from '../../shared/formats';

/**
 * One card on the tab. The page renders whatever `jobCards()` returns, so a new check is a new
 * field on MonitoringStatusDto plus one entry in that list -- no template change.
 */
type JobCard = {
  readonly key: string;
  readonly title: string;
  /** What the job does and how often, so a red card is readable without asking anyone. */
  readonly cadence: string;
  readonly state: MonitoringStateDto;
  readonly headline: string;
  readonly lastCompletedAt: string | undefined;
  readonly threshold: string;
};

function formatAge(ageMinutes: number | undefined): string {
  if (ageMinutes === undefined) {
    return 'Never';
  }
  if (ageMinutes < 1) {
    return 'Just now';
  }
  if (ageMinutes < 60) {
    return `${ageMinutes} min ago`;
  }

  const hours = Math.floor(ageMinutes / 60);
  if (hours < 24) {
    const minutes = ageMinutes % 60;
    return minutes === 0 ? `${hours}h ago` : `${hours}h ${minutes}m ago`;
  }

  const days = Math.floor(hours / 24);
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

function formatThreshold(staleAfterMinutes: number): string {
  const hours = staleAfterMinutes / 60;
  return hours >= 1 ? `Red after ${hours}h` : `Red after ${staleAfterMinutes} min`;
}

function toJobCard(
  key: string,
  title: string,
  cadence: string,
  freshness: JobFreshnessDto
): JobCard {
  return {
    key,
    title,
    cadence,
    state: freshness.state,
    headline: formatAge(freshness.ageMinutes),
    lastCompletedAt: freshness.lastCompletedAt,
    threshold: formatThreshold(freshness.staleAfterMinutes),
  };
}

@Component({
  selector: 'homey-monitoring-page',
  standalone: true,
  imports: [DatePipe],
  templateUrl: './monitoring-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MonitoringPageComponent {
  private readonly monitoringClient = inject(MonitoringClient);
  private readonly messageService = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly status = signal<MonitoringStatusDto | undefined>(undefined);
  protected readonly loading = signal(true);
  protected readonly dateTimeFormat = DATE_TIME_FORMAT;

  protected readonly jobCards = computed<JobCard[]>(() => {
    const status = this.status();
    if (status === undefined) {
      return [];
    }

    return [
      toJobCard(
        'lottery-sync',
        'Lottery sync',
        'Picks up new and closed lotteries from NYC. Runs hourly.',
        status.lotterySync
      ),
    ];
  });

  constructor() {
    this.loadStatus();
  }

  private loadStatus() {
    this.loading.set(true);
    this.monitoringClient
      // silentErrors: the catch block below reports this in the page's own words, so the
      // interceptor's generic toast would just stack a second one on top.
      .getMonitoringStatus('body', false, {context: silentErrors()})
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: status => this.status.set(status),
        error: () => {
          // Drop any previous reading: on a monitoring page a stale value presented as current is
          // worse than no value.
          this.status.set(undefined);
          this.messageService.add({
            severity: 'error',
            summary: 'Unable to load monitoring status',
          });
        },
      });
  }
}
